import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { RichShipDeckSurface3D } from 'features/assetViz/objects/surfaces/ShipDeckSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import { forecastMath } from 'features/math'
import React from 'react'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoEditableComponent,
  GeckoMeasurementUnit,
  GeckoShipDeck,
} from 'types'

export const ShipSceneConfig: SceneFactoryConfig = {
  name: 'Ship',
  assetModels: [GeckoAssetModelType.Ship],
  componentModels: [GeckoComponentModelType.ShipDeck],
  sceneFunc: (props) => <ShipScene {...props} />,
}

/**
 * A scene depicting a ship (or rather, a collection of ship decks)
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 */
const { pi } = forecastMath

export const ShipScene: React.FC<{
  componentModel: GeckoEditableComponent
}> = ({ componentModel }) => {
  const selectedModel = componentModel as GeckoShipDeck

  //----------------------------------------------------------------------------
  const { componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    const componentBounds =
      selectedModel.componentBounds ??
      GeckoComponentRect.CreateFromXYs(
        '',
        0,
        0,
        1,
        1,
        GeckoMeasurementUnit.Meters,
        GeckoMeasurementUnit.Meters
      )
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Meters ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Ship decks in the asset model should always be in meters, but 
              the x range is in ${componentBounds.x.unit}
              and y range is in ${componentBounds.y.unit}`
      )
    return componentBounds
  }, [selectedModel.componentBounds])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // This starts the camera off wherever there's data to look at
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentDataFrames, componentBounds]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
    }),
    [componentBounds]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num
      return {
        position: [relX, relY, distToComponent],
        rotation: [0, 0, pi],
      }
    },
    [componentBounds]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => [rect.width.num, rect.height.num],
    []
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getCameraDimensionsFit={getCameraDimensionsFit}
        getMovementForCursorDelta={([x, y]) => [-x, -y]}
      />
      {selectedModel instanceof GeckoShipDeck ? (
        <RichShipDeckSurface3D model={selectedModel} />
      ) : null}
    </>
  )
}
